/**
 * utmService.js — Sprint 26-1
 * UTM 파라미터 캡처 + localStorage 저장 + 가입 시 백엔드 전송
 *
 * 위치: phase-web/src/services/utmService.js
 *
 * 흐름:
 * 1. 사용자가 광고 클릭 → pause.ai.kr/?utm_source=meta&utm_campaign=chart 진입
 * 2. captureUtmParams() 자동 실행 → localStorage 저장
 * 3. 가입 시 getStoredUtm() 호출 → 백엔드에 함께 전송
 * 4. 백엔드: profiles 테이블 utm_* 컬럼에 저장
 *
 * ─── 핫픽스 (Sprint 26-4): 2026-06-15 ───
 * 기존 sessionStorage는 탭 닫으면 사라져서 광고→사이트 둘러봄→다음 날 재방문→가입
 * 흐름에서 UTM 누락 발생. 모바일 광고 사용자 패턴(인스타 In-App → 일반 브라우저 이동
 * 또는 며칠 후 재방문)을 못 따라감.
 *
 * 변경:
 * - sessionStorage → localStorage (브라우저 닫아도 UTM 보존)
 * - URL에 새 UTM이 있으면 기존 값 덮어쓰기 (재캠페인 추적 가능)
 *
 * 표준 UTM 5개 + Pause 전용 추가 추적:
 * - utm_source     플랫폼 (meta, naver, google, instagram)
 * - utm_medium     매체 (cpc, banner, video, organic)
 * - utm_campaign   캠페인 (chart, stop_loss, dca, sns_dependent)
 * - utm_content    소재 변형 (v1, v2, v3)
 * - utm_term       검색 키워드 (검색 광고 시)
 * - referrer       이전 페이지 (자동 수집)
 * - landing_page   최초 진입 페이지
 * - first_visit_at 최초 방문 시각
 */

const UTM_STORAGE_KEY = "pause_utm_data";
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

/**
 * URL에서 UTM 파라미터 추출 + localStorage 저장
 * 앱 진입 시 (App.jsx 또는 main.jsx) 한 번 호출.
 *
 * 정책 (Sprint 26-4):
 * - URL에 새 UTM이 있으면 → 기존 값 덮어쓰기 (재캠페인 클릭 추적)
 * - URL에 UTM 없는데 기존에 저장된 값 있으면 → 기존 유지 (재방문 시 추적 유지)
 * - URL에 UTM 없고 기존도 없으면 → "direct"로 저장
 */
export function captureUtmParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const hasNewUtm = UTM_PARAMS.some((key) => params.get(key));

    // URL에 새 UTM 없으면 → 기존 값 유지 (있으면)
    if (!hasNewUtm) {
      const existing = getStoredUtm();
      if (existing && existing.utm_source && existing.utm_source !== "direct") {
        return existing;
      }
    }

    const utmData = {};
    let hasUtm = false;

    // UTM 5개 추출
    UTM_PARAMS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        utmData[key] = value.slice(0, 100); // SQL injection 방지 + 길이 제한
        hasUtm = true;
      }
    });

    // 직접 방문(UTM 없음)도 추적
    if (!hasUtm) {
      utmData.utm_source = "direct";
    }

    // 추가 메타 데이터
    utmData.referrer = document.referrer ? document.referrer.slice(0, 200) : "";
    utmData.landing_page = window.location.pathname.slice(0, 100);
    utmData.first_visit_at = new Date().toISOString();

    // localStorage 저장 (브라우저 닫아도 보존 = 광고 후 재방문 가입 추적 가능)
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));

    if (hasUtm) {
      console.log("[UTM] Captured:", utmData);
    }

    return utmData;
  } catch (e) {
    console.error("[UTM] Capture failed:", e);
    return null;
  }
}

/**
 * 저장된 UTM 데이터 반환
 * 가입/결제 API 호출 시 함께 전송용
 */
export function getStoredUtm() {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.error("[UTM] Read failed:", e);
    return null;
  }
}

/**
 * 가입/결제 API 호출 시 사용 — UTM 데이터를 body에 포함
 *
 * 사용 예:
 *   const utm = getStoredUtm();
 *   await api("/auth/signup", {
 *     body: JSON.stringify({ email, password, ...utm })
 *   });
 */
export function attachUtmToBody(body = {}) {
  const utm = getStoredUtm();
  if (!utm) return body;

  return {
    ...body,
    utm_source: utm.utm_source || null,
    utm_medium: utm.utm_medium || null,
    utm_campaign: utm.utm_campaign || null,
    utm_content: utm.utm_content || null,
    utm_term: utm.utm_term || null,
    referrer: utm.referrer || null,
    landing_page: utm.landing_page || null,
  };
}

/**
 * UTM 정리 (가입 완료 후 호출하면 새 세션에서 추적 리셋 가능)
 * 일반적으로 사용 안 함.
 * Sprint 26-4 이후 localStorage라 명시적 호출 없으면 영구 보존.
 */
export function clearStoredUtm() {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
  } catch (e) {
    console.error("[UTM] Clear failed:", e);
  }
}
